import { withAlpha } from '../../piano/render/pianoRenderColour';
import { bigBannerScale, countdownNumberAnim, goBannerAnim } from '../../piano/render/pianoAnimCurves';
import { GO_BANNER_LIFE } from '../../piano/render/pianoRenderConstants';
import { computeLayout, type GuitarLayout } from '../guitarPlayfieldGeometry';
import type { GuitarChart } from '@shared/guitarChart';
import type {
  GuitarFloatingText,
  GuitarRenderInput,
  GuitarScorePop,
} from './guitarRenderTypes';
import type { PianoFxSystem } from '../../piano/render/pianoFx';
import type { ManiaLane } from '@shared/maniaNote';
import { MANIA_LANE_COUNT } from '@shared/maniaNote';
import type { RenderQuality } from '../../rendering/renderQuality';
import {
  COUNTDOWN_RING_COUNT,
  COUNTDOWN_RING_END_R,
  COUNTDOWN_RING_LIFE_S,
  COUNTDOWN_RING_START_R,
  LANE_GAP_PX,
} from './guitarRenderConstants';

/**
 * Top-of-screen overlays that aren't part of the highway / gem / HUD
 * vocabulary: floating verdict pops, +N score flyers, the lead-in
 * countdown number, the "GO!" banner the renderer pops itself, and
 * the paused dim overlay.
 *
 * `LeadInBannerState` is exposed so the orchestrator can keep one
 * instance across frames — it tracks the countdown→null transition
 * and ages the "GO!" banner toward expiry.
 */

interface CountdownRing {
  life: number;
  maxLife: number;
}

export class GuitarLeadInBannerState {
  private lastCountdown: number | null = null;
  private lastShownInteger: number | null = null;
  private goPulseRemaining = 0;
  private rings: CountdownRing[] = [];
  /** First-sight tracker for big banners — fires a one-shot entry shockwave. */
  readonly consumedBigBanners = new WeakSet<GuitarFloatingText>();

  reset(): void {
    this.lastCountdown = null;
    this.lastShownInteger = null;
    this.goPulseRemaining = 0;
    this.rings.length = 0;
  }

  advance(countdown: number | null, dt: number): void {
    const prev = this.lastCountdown;
    this.lastCountdown = countdown;
    if (prev !== null && prev > 0 && (countdown === null || countdown <= 0)) {
      this.goPulseRemaining = GO_BANNER_LIFE;
      this.spawnRingBurst();
    }
    if (countdown !== null && countdown > 0) {
      const currentInteger = Math.max(1, Math.ceil(countdown));
      if (this.lastShownInteger !== currentInteger) {
        this.lastShownInteger = currentInteger;
        this.spawnRingBurst();
      }
    } else {
      this.lastShownInteger = null;
    }
    if (dt > 0) {
      if (this.goPulseRemaining > 0) {
        this.goPulseRemaining = Math.max(0, this.goPulseRemaining - dt);
      }
      for (let i = this.rings.length - 1; i >= 0; i -= 1) {
        this.rings[i].life -= dt;
        if (this.rings[i].life <= 0) {
          this.rings.splice(i, 1);
        }
      }
    }
  }

  shouldDrawCountdown(countdown: number | null): boolean {
    return (
      (countdown !== null && countdown > 0) ||
      this.goPulseRemaining > 0 ||
      this.rings.length > 0
    );
  }

  getGoPulseRemaining(): number {
    return this.goPulseRemaining;
  }

  getCountdownRings(): ReadonlyArray<CountdownRing> {
    return this.rings;
  }

  private spawnRingBurst(): void {
    for (let i = 0; i < COUNTDOWN_RING_COUNT; i += 1) {
      // Each ring in the burst is delayed slightly so they form a
      // staggered triplet rather than three concentric rings at the
      // same radius. Negative starting `life` lets the ring tick
      // forward via `advance(dt)` before becoming visible.
      const stagger = i * (COUNTDOWN_RING_LIFE_S * 0.18);
      this.rings.push({
        life: COUNTDOWN_RING_LIFE_S - stagger,
        maxLife: COUNTDOWN_RING_LIFE_S,
      });
    }
  }
}

export function drawGuitarFloatingTexts(
  ctx: CanvasRenderingContext2D,
  input: GuitarRenderInput,
  layout: GuitarLayout,
  canvasWidth: number,
  canvasHeight: number,
  fx: PianoFxSystem,
  bannerState: GuitarLeadInBannerState,
  reducedMotion: boolean,
  quality: RenderQuality,
): void {
  if (input.floatingTexts.length === 0) {
    return;
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Banner Y placement same as Piano — top-third area where gems
  // haven't yet emerged from the back wall, so banners don't occlude
  // approaching notes.
  const bannerY = canvasHeight * 0.18;
  const bottomStackLeft = layout.centreX - layout.bottomStackWidth / 2;
  for (const t of input.floatingTexts) {
    const alpha = Math.max(0, Math.min(1, t.life / t.maxLife));
    if (t.big) {
      const elapsed = 1 - alpha;
      const scale = reducedMotion ? 1 : bigBannerScale(elapsed);
      const fontPx = Math.round(36 * scale);
      if (!bannerState.consumedBigBanners.has(t)) {
        bannerState.consumedBigBanners.add(t);
        fx.spawnShockwaveDirect({
          x: canvasWidth / 2,
          y: bannerY,
          startR: 24,
          endR: 220,
          life: 0.45,
          thickness: 3,
          colour: t.colour,
          alpha: 0.75,
        });
      }
      ctx.save();
      ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
      ctx.fillStyle = withAlpha(t.colour, alpha);
      if (!reducedMotion && quality.chromeShadows) {
        ctx.shadowColor = t.colour;
        ctx.shadowBlur = 18 * alpha;
      }
      ctx.fillText(t.text, canvasWidth / 2, bannerY);
      ctx.restore();
    } else {
      const fontPx = t.emphasised ? 28 : 20;
      ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
      ctx.fillStyle = withAlpha(t.colour, alpha);
      const lane = t.lane as ManiaLane;
      const x =
        bottomStackLeft +
        lane * (layout.bottomLaneWidth + LANE_GAP_PX) +
        layout.bottomLaneWidth / 2;
      if (t.emphasised && !reducedMotion && quality.chromeShadows) {
        ctx.save();
        ctx.shadowColor = t.colour;
        ctx.shadowBlur = 14 * alpha;
        ctx.fillText(t.text, x, t.y);
        ctx.restore();
      } else {
        ctx.fillText(t.text, x, t.y);
      }
    }
  }
  // Compiler hint: ensure MANIA_LANE_COUNT import is used somewhere
  // observable so a future reshape doesn't drop the import quietly.
  void MANIA_LANE_COUNT;
}

export function drawGuitarScorePops(
  ctx: CanvasRenderingContext2D,
  pops: ReadonlyArray<GuitarScorePop>,
  reducedMotion: boolean,
  quality: RenderQuality,
): void {
  if (pops.length === 0) {
    return;
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const allowGlow = !reducedMotion && quality.chromeShadows;
  for (const pop of pops) {
    const t = 1 - pop.life / pop.maxLife;
    const easeOut = 1 - Math.pow(1 - t, 3);
    const x = pop.startX + (pop.targetX - pop.startX) * easeOut;
    const arcLift = Math.sin(t * Math.PI) * 40;
    const y = pop.startY + (pop.targetY - pop.startY) * easeOut - arcLift;
    const alpha = Math.max(0, Math.min(1, pop.life / pop.maxLife));
    const size = pop.big ? 22 : 16;
    ctx.font = `bold ${size}px system-ui, sans-serif`;
    ctx.fillStyle = withAlpha(pop.colour, alpha);
    // Skip shadow blur on low tier — colour + arc motion alone
    // still telegraphs the reward without the Gaussian pass.
    if (pop.big && allowGlow) {
      ctx.save();
      ctx.shadowColor = pop.colour;
      ctx.shadowBlur = 10 * alpha;
      ctx.fillText(pop.text, x, y);
      ctx.restore();
    } else {
      ctx.fillText(pop.text, x, y);
    }
  }
}

export function drawGuitarCountdown(
  ctx: CanvasRenderingContext2D,
  countdown: number | null,
  width: number,
  height: number,
  palette: GuitarChart['palette'],
  goPulseRemaining: number,
  countdownRings: ReadonlyArray<{ life: number; maxLife: number }>,
  reducedMotion: boolean,
  quality: RenderQuality,
): void {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const centreX = width / 2;
  const centreY = height / 2;
  const allowGlow = !reducedMotion && quality.chromeShadows;

  if (!reducedMotion && countdownRings.length > 0) {
    drawCountdownRings(ctx, countdownRings, centreX, centreY, palette);
  }

  if (countdown !== null && countdown > 0) {
    const number = Math.max(1, Math.ceil(countdown));
    const tWithin = Math.max(0, Math.min(1, number - countdown));
    const { scale, alpha } = countdownNumberAnim(tWithin, reducedMotion);
    if (alpha > 0.01) {
      const fontPx = Math.round(80 * scale);
      ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
      // Cyan + magenta chromatic ghosts on the punch-in plateau.
      // Skipped on low tier alongside the shadow glow.
      if (allowGlow && tWithin < 0.55) {
        const offset = 6 * (1 - tWithin / 0.55);
        ctx.save();
        ctx.globalAlpha = alpha * 0.55;
        ctx.fillStyle = '#3bf0ff';
        ctx.fillText(String(number), centreX - offset, centreY);
        ctx.fillStyle = '#ff3bdc';
        ctx.fillText(String(number), centreX + offset, centreY);
        ctx.restore();
      }
      ctx.fillStyle = withAlpha(palette.ui, alpha);
      if (allowGlow) {
        ctx.save();
        ctx.shadowColor = palette.ui;
        ctx.shadowBlur = 18 * alpha;
        ctx.fillText(String(number), centreX, centreY);
        ctx.restore();
      } else {
        ctx.fillText(String(number), centreX, centreY);
      }
    }
  }

  if (goPulseRemaining > 0) {
    const t = 1 - goPulseRemaining / GO_BANNER_LIFE;
    const { scale, alpha } = goBannerAnim(t, reducedMotion);
    if (alpha > 0.01) {
      const fontPx = Math.round(88 * scale);
      ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
      const goColour = palette.padPerfect;
      // Chromatic-ghost treatment matching the countdown number;
      // gated on chromeShadows so low tier skips the extra fillTexts.
      if (allowGlow && t < 0.5) {
        const offset = 8 * (1 - t / 0.5);
        ctx.save();
        ctx.globalAlpha = alpha * 0.55;
        ctx.fillStyle = '#3bf0ff';
        ctx.fillText('GO!', centreX - offset, centreY);
        ctx.fillStyle = '#ff3bdc';
        ctx.fillText('GO!', centreX + offset, centreY);
        ctx.restore();
      }
      ctx.fillStyle = withAlpha(goColour, alpha);
      if (allowGlow) {
        ctx.save();
        ctx.shadowColor = goColour;
        ctx.shadowBlur = 32 * alpha;
        ctx.fillText('GO!', centreX, centreY);
        ctx.restore();
      } else {
        ctx.fillText('GO!', centreX, centreY);
      }
    }
  }
}

function drawCountdownRings(
  ctx: CanvasRenderingContext2D,
  rings: ReadonlyArray<{ life: number; maxLife: number }>,
  centreX: number,
  centreY: number,
  palette: GuitarChart['palette'],
): void {
  ctx.save();
  ctx.lineWidth = 3;
  for (const ring of rings) {
    if (ring.life <= 0) {
      continue;
    }
    const lifeFrac = Math.max(0, Math.min(1, ring.life / ring.maxLife));
    const eased = 1 - lifeFrac;
    const radius = COUNTDOWN_RING_START_R + (COUNTDOWN_RING_END_R - COUNTDOWN_RING_START_R) * eased;
    const alpha = lifeFrac * lifeFrac;
    if (alpha < 0.02) {
      continue;
    }
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = palette.padPerfect;
    ctx.beginPath();
    ctx.arc(centreX, centreY, radius, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

export function drawGuitarPausedOverlay(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  palette: GuitarChart['palette'],
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.fillRect(0, 0, width, height);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = palette.ui;
  ctx.font = 'bold 36px system-ui, sans-serif';
  ctx.fillText('Paused', width / 2, height / 2);
}

export { computeLayout };
